import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Eye, Flame, AlertTriangle, XCircle, CheckCircle, Clock, ShieldAlert, Dna, Zap, HelpCircle, AlertOctagon } from 'lucide-react';
export function NewProblemSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref2, inView2] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref3, inView3] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref4, inView4] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref5, inView5] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref6, inView6] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref7, inView7] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref8, inView8] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref9, inView9] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref10, inView10] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref11, inView11] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref12, inView12] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref13, inView13] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref14, inView14] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  const [ref15, inView15] = useInView({
    triggerOnce: true,
    threshold: 0.01
  });
  return <section className="relative pt-8 sm:pt-12 pb-8 sm:pb-12 px-4 sm:px-6 bg-black overflow-hidden" data-id="element-2852">
      {/* DRAMATIC VAULT TRANSFORMATION - Bold and unmistakable */}

      {/* Deep vault background - much darker than hero */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-neutral-950 to-black" data-id="element-2853" />

      {/* Heavy concrete texture - very visible */}
      <div className="absolute inset-0 opacity-20" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='400' height='400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' /%3E%3C/filter%3E%3Crect width='400' height='400' filter='url(%23noise)' opacity='0.7'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat'
    }} data-id="element-2854" />

      {/* BOLD security grid - clearly visible */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: `
            linear-gradient(rgba(217, 119, 6, 0.6) 2px, transparent 2px),
            linear-gradient(90deg, rgba(217, 119, 6, 0.6) 2px, transparent 2px)
          `,
      backgroundSize: '80px 80px'
    }} data-id="element-2855" />

      {/* MASSIVE vault door frame - unmistakable borders */}
      <div className="absolute left-0 inset-y-0 w-8 bg-gradient-to-r from-stone-700 via-stone-800 to-transparent hidden lg:block" data-id="element-2856" />
      <div className="absolute right-0 inset-y-0 w-8 bg-gradient-to-l from-stone-700 via-stone-800 to-transparent hidden lg:block" data-id="element-2857" />

      {/* TOP SECRET corner stamps - bold and visible */}
      <div className="absolute top-12 left-12 hidden lg:block" data-id="element-2858">
        <div className="relative w-32 h-32 border-4 border-amber-600/60 rotate-12 flex items-center justify-center" data-id="element-2859">
          <div className="absolute inset-0 bg-amber-600/10" data-id="element-2860" />
          <span className="text-amber-500 font-bold text-xs tracking-widest rotate-[-12deg]" data-id="element-2861">
            CLASSIFIED
          </span>
        </div>
      </div>
      <div className="absolute top-12 right-12 hidden lg:block" data-id="element-2862">
        <div className="relative w-32 h-32 border-4 border-red-600/60 rotate-[-12deg] flex items-center justify-center" data-id="element-2863">
          <div className="absolute inset-0 bg-red-600/10" data-id="element-2864" />
          <span className="text-red-500 font-bold text-xs tracking-widest rotate-12" data-id="element-2865">
            RESTRICTED
          </span>
        </div>
      </div>

      {/* Heavy vault door bolts on sides */}
      <div className="absolute left-2 sm:left-4 top-1/4 hidden lg:flex flex-col gap-16" data-id="element-2866">
        {[...Array(3)].map((_, i) => <div key={i} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-stone-600 to-stone-800 border-2 border-amber-700/40 shadow-lg" data-id="element-2867">
            <div className="w-full h-full rounded-full bg-gradient-to-br from-stone-700 to-stone-900 m-0.5" data-id="element-2868" />
          </div>)}
      </div>
      <div className="absolute right-2 sm:right-4 top-1/4 hidden lg:flex flex-col gap-16" data-id="element-2869">
        {[...Array(3)].map((_, i) => <div key={i} className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-stone-600 to-stone-800 border-2 border-amber-700/40 shadow-lg" data-id="element-2870">
            <div className="w-full h-full rounded-full bg-gradient-to-br from-stone-700 to-stone-900 m-0.5" data-id="element-2871" />
          </div>)}
      </div>

      {/* STRONG vignette - vault entrance */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(0,0,0,0.4)_70%,_rgba(0,0,0,0.8)_100%)] pointer-events-none" data-id="element-2872" />

      {/* Dark top shadow - descending into vault */}
      <div className="absolute top-0 inset-x-0 h-32 sm:h-48 bg-gradient-to-b from-black via-black/60 to-transparent pointer-events-none" data-id="element-2873" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 20
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-2874">
        {/* Header */}
        <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8" data-id="element-2875">
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" data-id="element-2876" />
          <Eye className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" data-id="element-2877" />
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-transparent" data-id="element-2878" />
        </div>

        <h2 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-serif mb-8 sm:mb-12 text-center text-stone-100 leading-tight" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 40px rgba(217, 119, 6, 0.3)'
      }} data-id="element-2879">
          The Future Is Becoming{' '}
          <span className="inline-block bg-gradient-to-r from-stone-100 via-orange-400 to-red-500 bg-clip-text text-transparent font-bold" style={{
          textShadow: '0 0 30px rgba(249, 115, 22, 0.5)'
        }} data-id="element-2880">
            Increasingly
          </span>{' '}
          <span className="inline-block text-red-500 font-extrabold" style={{
          textShadow: '0 0 60px rgba(220, 38, 38, 0.8), 0 0 100px rgba(220, 38, 38, 0.4)'
        }} data-id="element-2881">
            Fake
          </span>
        </h2>

        <div className="space-y-6 sm:space-y-8 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-2882">
          <p className="text-stone-100 text-center text-lg sm:text-xl" data-id="element-2883">
            And Your Biology Is Paying The Price
          </p>

          <p className="text-xl md:text-2xl font-bold text-stone-100" data-id="element-2884">
            Here's what's happening right now while you read this:
          </p>

          {/* AI Hallucinations */}
          <div className="space-y-3 sm:space-y-4" data-id="element-2885">
            <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-2886">
              AI chatbots are confidently hallucinating garbage health advice
              that is being spread and used as material by... More AI chatbots.
            </p>
            <p className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 text-base sm:text-lg" data-id="element-2887">
              One wrong Reddit post gets cited by a blogger, quoted by a
              YouTuber, summarized by ChatGPT, and trusted by thousands who
              think they're getting the truth.
            </p>
          </div>

          {/* Influencers */}
          <div className="space-y-3 sm:space-y-4" data-id="element-2888">
            <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-2889">
              Influencers are optimizing for views and engagement, not accuracy
              and outcomes.
            </p>
            <p className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 text-base sm:text-lg" data-id="element-2890">
              They're teaching peptide "stacks" they don't understand because
              the algorithm rewards confident simplicity over nuanced precision.
            </p>
          </div>

          {/* Doctors */}
          <div className="space-y-3 sm:space-y-4" data-id="element-2891">
            <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-2892">
              Even qualified doctors are operating with fragments of knowledge,
              regurgitating what they heard from someone who heard it from
              someone else.
            </p>
            <p className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 italic text-stone-400 text-base sm:text-lg" data-id="element-2893">
              A broken telephone game with your biology as the stakes.
            </p>
          </div>

          {/* Getting worse */}
          <div className="space-y-3 sm:space-y-4 mt-8 sm:mt-12" data-id="element-2894">
            <p className="text-xl md:text-2xl font-bold text-stone-100" data-id="element-2895">
              But it's getting WORSE.
            </p>
            <p className="text-2xl md:text-3xl font-bold text-amber-500" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.3)'
          }} data-id="element-2896">
              Way worse.
            </p>
          </div>

          <p className="text-lg md:text-xl text-stone-100" data-id="element-2897">
            And if you don't have a trusted source of REAL intelligence...
          </p>

          <p className="text-2xl md:text-3xl font-bold text-center text-amber-500" style={{
          textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
        }} data-id="element-2898">
            You're gonna get buried.
          </p>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 mt-10 sm:mt-16" data-id="element-2899">
            <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-2900" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-2901" />
            <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-2902" />
          </div>

          {/* Section 2: Butterfly Effect */}
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-stone-100 mt-10 sm:mt-16 mb-8 sm:mb-12" style={{
          fontFamily: 'Cinzel, serif',
          textShadow: '0 0 30px rgba(217, 119, 6, 0.2)'
        }} data-id="element-2903">
            The Butterfly Effect of{' '}
            <span className="text-amber-500" style={{
            textShadow: '0 0 40px rgba(217, 119, 6, 0.5)'
          }} data-id="element-2904">
              AI Hallucinations
            </span>
          </h2>

          {/* Turing quote */}
          <div className="bg-stone-950/50 border-l-4 border-amber-600 p-6 sm:p-8 my-6 sm:my-8" data-id="element-2905">
            <p className="text-sm sm:text-base text-stone-400 mb-2" data-id="element-2906">
              In 1950, Alan Turing (one of the "godfathers" of modern AI and
              computing) noted:
            </p>
            <p className="text-lg md:text-xl text-stone-100 italic leading-relaxed" data-id="element-2907">
              "The displacement of a single electron by a billionth of a
              centimetre at one moment might make the difference between a man
              being killed by an avalanche a year later, or escaping."
            </p>
          </div>

          {/* Alan Turing Portrait */}
          <div className="flex justify-center my-8" data-id="element-2908">
            <div className="relative inline-block" data-id="element-2909">
              {/* Ornate oval frame */}
              <div className="relative w-48 h-64 md:w-56 md:h-72" data-id="element-2910">
                {/* Outer glow */}
                <div className="absolute inset-0 rounded-[50%] bg-gradient-to-b from-amber-600/20 to-amber-900/20 blur-xl" data-id="element-2911" />

                {/* Frame border */}
                <div className="absolute inset-0 rounded-[50%] border-4 border-amber-600/40 shadow-[0_0_30px_rgba(217,119,6,0.3)]" data-id="element-2912" />
                <div className="absolute inset-1 rounded-[50%] border-2 border-amber-500/20" data-id="element-2913" />

                {/* Image container */}
                <div className="absolute inset-3 rounded-[50%] overflow-hidden bg-stone-900" data-id="element-2914">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nuhSeJBYyic5QEPhD1HjfW/Alan_turing_header.jpg" alt="Alan Turing" className="w-full h-full object-cover grayscale" data-id="element-2915" />
                </div>
              </div>

              {/* Name plate */}
              <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-stone-950 border border-amber-600/50 px-4 py-1 whitespace-nowrap" data-id="element-2916">
                <p className="text-sm text-amber-500 font-semibold tracking-wide" data-id="element-2917">
                  Alan Turing
                </p>
              </div>
            </div>
          </div>

          <p className="text-xl font-bold text-stone-100" data-id="element-2918">
            He was describing chaos theory, and what would later come to be
            known as the "butterfly effect."
          </p>

          <p className="text-lg md:text-xl text-stone-100" data-id="element-2919">
            Small changes.{' '}
            <span className="text-amber-500 font-bold" data-id="element-2920">
              Massive consequences.
            </span>
          </p>

          {/* Cascade effect */}
          <div className="space-y-4 pl-6 border-l-2 border-amber-600/30" data-id="element-2921">
            <p data-id="element-2922">A slight error in a large language model's output...</p>
            <p data-id="element-2923">Gets repeated by thousands of people who trust it...</p>
            <p data-id="element-2924">
              Gets scraped by OTHER AI tools that use it to generate MORE
              content...
            </p>
          </div>

          <p className="text-xl md:text-2xl font-bold text-stone-100" data-id="element-2925">
            And suddenly, the entire internet is littered with confidently
            incorrect information that FEELS true... but isn't.
          </p>

          {/* Transition to next section */}
          <div className="relative bg-gradient-to-br from-amber-950/30 to-black border-2 border-amber-600 p-6 sm:p-8 md:p-10 mt-12 sm:mt-16" data-id="element-2926">
            <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-amber-600" data-id="element-2927" />
            <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-amber-600" data-id="element-2928" />
            <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-amber-600" data-id="element-2929" />
            <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-amber-600" data-id="element-2930" />

            <p className="text-xl sm:text-2xl md:text-3xl text-center text-amber-500 font-bold leading-relaxed" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.5)'
          }} data-id="element-2931">
              The signal-to-noise ratio in biological optimization has reached a
              critical low.
            </p>
          </div>
        </div>
      </motion.div>

      {/* NEW SECTION: AI YouTube Channel Example */}
      <motion.div ref={ref9} initial={{
      opacity: 1,
      y: 0
    }} animate={inView9 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-2932">
        {/* Divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-2933">
          <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-2934" />
          <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-2935" />
          <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-2936" />
        </div>

        {/* Headline */}
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8 sm:mb-12" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
      }} data-id="element-2937">
          When I Saw the Image Below, I Realized{' '}
          <span className="text-red-500" data-id="element-2938">Humanity Was In Trouble</span>
        </h3>

        {/* Image */}
        <div className="relative mb-12" data-id="element-2939">
          <div className="absolute -inset-1 bg-gradient-to-r from-amber-600/20 to-red-600/20 rounded-lg blur" data-id="element-2940" />
          <div className="relative bg-stone-950 p-2 rounded-lg border border-amber-600/30" data-id="element-2941">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dJZbq3Yek5VH6X3WfXdXqi/b4e72e43-3622-4e89-a982-bb2047fc45f9-tinyfied.png" alt="AI-generated YouTube channel uploading daily" className="w-full rounded" data-id="element-2942" />
          </div>
        </div>

        {/* Body copy */}
        <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-2943">
          <p className="text-stone-100 font-semibold" data-id="element-2944">
            I came across this entire YouTube channel that was being run 100% by
            AI.
          </p>

          <div className="bg-stone-950/50 border-l-4 border-red-600/50 p-4 sm:p-6 space-y-3" data-id="element-2945">
            <p className="text-stone-100" data-id="element-2946">
              The script?{' '}
              <span className="font-bold text-red-400" data-id="element-2947">AI-generated.</span>
            </p>
            <p className="text-stone-100" data-id="element-2948">
              The voiceover?{' '}
              <span className="font-bold text-red-400" data-id="element-2949">AI-generated.</span>
            </p>
            <p className="text-stone-100" data-id="element-2950">
              The visuals?{' '}
              <span className="font-bold text-red-400" data-id="element-2951">AI-generated.</span>
            </p>
            <p className="text-stone-100" data-id="element-2952">
              The thumbnails?{' '}
              <span className="font-bold text-red-400" data-id="element-2953">AI-generated.</span>
            </p>
            <p className="text-stone-100" data-id="element-2954">
              The music?{' '}
              <span className="font-bold text-red-400" data-id="element-2955">AI-generated.</span>
            </p>
          </div>

          <p className="text-xl md:text-2xl font-bold text-stone-100" data-id="element-2956">
            It was churning out{' '}
            <span className="text-red-500" style={{
            textShadow: '0 0 20px rgba(239, 68, 68, 0.4)'
          }} data-id="element-2957">
              VIRAL
            </span>{' '}
            content. <span className="text-amber-500" data-id="element-2958">Every. Single. Day.</span>
          </p>

          <div className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 space-y-3" data-id="element-2959">
            <p data-id="element-2960">Long videos.</p>
            <p data-id="element-2961">Thousands of views.</p>
            <p data-id="element-2962">Climbing the algorithm.</p>
          </div>

          <p className="text-2xl md:text-3xl font-bold text-center text-red-500 mt-6 sm:mt-8" data-id="element-2963">
            And almost{' '}
            <span style={{
            textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
          }} data-id="element-2964">
              NOBODY
            </span>{' '}
            in the comments realized it was fake.
          </p>

          {/* NEW SECTION: FDR Hallucination */}
          <div className="space-y-8 sm:space-y-12 mt-12 sm:mt-16" data-id="element-2965">
            <p className="text-2xl md:text-3xl font-bold text-stone-100" data-id="element-2966">
              But here's where it gets{' '}
              <span className="text-red-500" style={{
              textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
            }} data-id="element-2967">
                insidious
              </span>
              ...
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2968">
              In one of the videos, the AI hallucinated a "fact."
            </p>

            <p className="text-lg sm:text-xl text-stone-100 border-l-4 border-red-600 pl-4 sm:pl-6 py-2 bg-red-950/10" data-id="element-2969">
              It confidently stated that{' '}
              <span className="font-bold text-red-400" data-id="element-2970">
                Franklin Delano Roosevelt
              </span>{' '}
              is the president on Mount Rushmore.
            </p>

            {/* FDR Image */}
            <div className="relative my-8" data-id="element-2971">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600/20 to-amber-600/20 rounded-lg blur" data-id="element-2972" />
              <div className="relative bg-stone-950 p-2 rounded-lg border border-red-600/30" data-id="element-2973">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aFRrizYEorcBCvdGpoiDKS/FDR-Visual-tinyfied.png" alt="AI-generated FDR visual from video" className="w-full rounded" data-id="element-2974" />
              </div>
            </div>

            <p className="text-2xl md:text-3xl font-bold text-stone-100" data-id="element-2975">
              Except... FDR is{' '}
              <span className="text-red-500 uppercase" style={{
              textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
            }} data-id="element-2976">
                NOT
              </span>{' '}
              the Roosevelt on Mount Rushmore.
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-semibold pl-4 sm:pl-6 border-l-2 border-amber-600/30" data-id="element-2977">
              In reality that's{' '}
              <span className="text-amber-500 font-bold" data-id="element-2978">
                Theodore Roosevelt
              </span>
              .
            </p>

            <p className="text-xl sm:text-2xl font-bold text-stone-100" data-id="element-2979">
              But the AI didn't stop there.
            </p>

            <p className="text-lg sm:text-xl text-red-400 font-semibold" data-id="element-2980">
              It then GENERATED an image of FDR... ON Mount Rushmore.
            </p>

            {/* FDR on Mount Rushmore Image */}
            <div className="relative my-8" data-id="element-2981">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600/30 to-red-600/30 rounded-lg blur-lg" data-id="element-2982" />
              <div className="relative bg-stone-950 p-2 rounded-lg border-2 border-red-600/50" data-id="element-2983">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/fRUyWfrFdHjTMpduYvvgb3/FDR-On-Mt-Rushmore-tinyfied.png" alt="AI-generated image of FDR on Mount Rushmore" className="w-full rounded" data-id="element-2984" />
              </div>
            </div>

            <p className="text-2xl md:text-3xl font-bold text-center text-red-500" style={{
            textShadow: '0 0 40px rgba(239, 68, 68, 0.6)'
          }} data-id="element-2985">
              Reinforcing its OWN hallucination.
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-semibold mt-6 sm:mt-8" data-id="element-2986">
              And the comments?
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-2987">
              Almost nobody caught it.
            </p>

            <p className="text-3xl md:text-4xl font-bold text-center text-stone-100 mt-6 sm:mt-8" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.3)'
          }} data-id="element-2988">
              People just... believed it.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 my-8 sm:my-12" data-id="element-2989">
            <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-red-600/50 to-red-600/50" data-id="element-2990" />
            <div className="w-2 h-2 bg-red-600 rotate-45" data-id="element-2991" />
            <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-red-600/50 to-red-600/50" data-id="element-2992" />
          </div>

          {/* Health Connection */}
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8 sm:mb-12" style={{
          fontFamily: 'Cinzel, serif',
          textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
        }} data-id="element-2993">
            But It's Not Just History. It's Your{' '}
            <span className="text-red-500" style={{
            textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
          }} data-id="element-2994">
              HEALTH
            </span>
            .
          </h3>

          <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-2995">
            <p className="text-xl md:text-2xl text-stone-100 font-semibold" data-id="element-2996">
              Now imagine that same process happening with PEPTIDES.
            </p>

            <div className="pl-4 sm:pl-6 border-l-2 border-red-600/50 space-y-4 text-base sm:text-lg" data-id="element-2997">
              <p className="text-stone-100" data-id="element-2998">
                With molecules you're putting into{' '}
                <span className="font-bold text-red-400" data-id="element-2999">YOUR</span> body.
              </p>
              <p className="text-stone-100" data-id="element-3000">
                With advice that affects{' '}
                <span className="font-bold text-red-400" data-id="element-3001">YOUR</span> brain,{' '}
                <span className="font-bold text-red-400" data-id="element-3002">YOUR</span> organs,{' '}
                <span className="font-bold text-red-400" data-id="element-3003">YOUR</span> wellbeing.
              </p>
            </div>

            <p className="text-xl md:text-2xl font-bold text-stone-100" data-id="element-3004">
              Because it's already happening.
            </p>

            <p className="text-2xl md:text-3xl font-bold text-center text-amber-500 mt-6 sm:mt-8" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3005">
              Let me show you.
            </p>
          </div>

          {/* Divider */}

          {/* Section 1: Elon/LeBron */}
          <div className="space-y-8" data-id="element-3006"></div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 mt-8 sm:mt-16" data-id="element-3007">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3008" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3009" />
            <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3010" />
          </div>

          {/* Section 2: BPC-157 vs Vesugen */}
          <div className="space-y-8" data-id="element-3011">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-center text-stone-100 leading-tight" style={{
            fontFamily: 'Cinzel, serif'
          }} data-id="element-3012">
              <span className="text-yellow-400" style={{
              textShadow: '0 0 30px rgba(234, 179, 8, 0.4)'
            }} data-id="element-3013">
                AI Does Not Know What It's Saying
              </span>
              {' - '}
              <span className="text-yellow-300" data-id="element-3014">Example #1</span>
            </h3>

            <div className="text-lg md:text-xl text-stone-300 space-y-6 leading-relaxed" data-id="element-3015">
              <p data-id="element-3016">
                ChatGPT was asked to list the best peptides for vascular health.
              </p>

              <p data-id="element-3017">
                It confidently listed{' '}
                <span className="text-stone-100 font-bold" data-id="element-3018">
                  BPC-157, TB-500, and others.
                </span>
              </p>

              <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-red-600 pl-4 sm:pl-6 py-2 bg-red-950/10" data-id="element-3019">
                But somehow it SKIPPED what should have been the absolute top
                recommendation – <span className="text-red-400" data-id="element-3020">Vesugen</span>.
              </p>

              {/* Image */}
              <div className="relative my-8 max-w-2xl mx-auto" data-id="element-3021">
                <div className="absolute -inset-1 bg-gradient-to-r from-red-600/20 to-amber-600/20 rounded-lg blur opacity-75" data-id="element-3022" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9d6nVvQcppnhCKKcbixi1c/ChatGPTSkippedVesugen-tinyfied.png" alt="ChatGPT skipping Vesugen" className="relative rounded-lg border border-stone-800 w-full" data-id="element-3023" />
              </div>

              <p data-id="element-3024">
                And when pressed, it claimed Vesugen didn't have enough
                scientific backing.
              </p>

              <p className="italic text-stone-400" data-id="element-3025">
                Except Brenden Henry provided{' '}
                <span className="text-stone-100 font-bold not-italic" data-id="element-3026">
                  60+ scientific citations
                </span>{' '}
                to real studies in a video proving Vesugen's effectiveness for
                vascular health.
              </p>

              <p data-id="element-3027">
                Most "influencers" and "experts" take the generic, surface-level
                approach of recommending BPC-157 for almost everything…
              </p>

              <p className="text-stone-100 font-bold" data-id="element-3028">
                And ChatGPT simply followed suit.
              </p>

              <p data-id="element-3029">
                It just... guessed. Based on all the data it's been trained on.
              </p>

              <p className="text-2xl font-bold text-red-500" data-id="element-3030">
                And it's largely BS.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 mt-8 sm:mt-16" data-id="element-3031">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3032" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3033" />
            <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3034" />
          </div>

          {/* Section 3: Inventing New Peptides */}
          <div className="space-y-8" data-id="element-3035">
            <h3 className="text-2xl md:text-3xl font-serif font-bold text-center text-stone-100 leading-tight" style={{
            fontFamily: 'Cinzel, serif'
          }} data-id="element-3036">
              <span className="text-yellow-400" style={{
              textShadow: '0 0 30px rgba(234, 179, 8, 0.4)'
            }} data-id="element-3037">
                AI Does Not Know What It's Saying
              </span>
              {' - '}
              <span className="text-yellow-300" data-id="element-3038">Example #2</span>
            </h3>

            <div className="text-lg md:text-xl text-stone-300 space-y-6 leading-relaxed" data-id="element-3039">
              <p data-id="element-3040">
                I recently asked ChatGPT for the best peptides for brain
                restoration.
              </p>

              <p className="font-bold text-stone-100" data-id="element-3041">It confidently listed:</p>

              <ul className="space-y-4 pl-4 border-l-2 border-stone-800 text-base sm:text-lg" data-id="element-3042">
                <li data-id="element-3043">
                  <span className="text-amber-500 font-bold" data-id="element-3044">Bronchogen</span>{' '}
                  <span className="text-stone-400" data-id="element-3045">
                    (which, at the time of writing, has zero evidence in
                    scientific literature that it does ANYTHING for the brain)
                  </span>
                </li>
                <li data-id="element-3046">
                  <span className="text-amber-500 font-bold" data-id="element-3047">Cardiogen</span>{' '}
                  <span className="text-stone-400" data-id="element-3048">
                    (while it's possible it could indirectly help restore the
                    brain, at the time of writing there's also no literature
                    backing up ChatGPT's assertion)
                  </span>
                </li>
              </ul>

              <p className="text-lg sm:text-xl text-stone-100 font-bold mt-6" data-id="element-3049">
                But then it listed a peptide called "Cortexgen"
              </p>

              {/* Image */}
              <div className="relative my-8 max-w-2xl mx-auto" data-id="element-3050">
                <div className="absolute -inset-1 bg-gradient-to-r from-amber-600/20 to-red-600/20 rounded-lg blur opacity-75" data-id="element-3051" />
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3nt8h71t9XkgavNxaiVqUD/ChatGPTCortexgenHallucination.png" alt="ChatGPT hallucinating Cortexgen" className="relative rounded-lg border border-stone-800 w-full" data-id="element-3052" />
              </div>

              <p className="text-2xl font-bold text-red-500 text-center" data-id="element-3053">
                Except "Cortexgen" doesn't exist.
              </p>

              <p className="text-center font-medium text-stone-100" data-id="element-3054">
                It just... made it up.
              </p>

              <p data-id="element-3055">
                Probably mashed together Cortexin and Cortagen in its neural
                network and spit out a fake name.
              </p>

              <p className="text-xl font-bold text-stone-100 border-b-2 border-amber-600/30 pb-2 inline-block" data-id="element-3056">
                But that's not the worst of it...
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 mt-16 sm:mt-20" data-id="element-3057">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3058" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3059" />
            <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3060" />
          </div>

          {/* NEW SECTION: And Humans Are Making It Worse */}
          <div className="space-y-8" data-id="element-3061">
            <h3 className="text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
          }} data-id="element-3062">
              Because Humans Are Making It{' '}
              <span className="text-red-500" style={{
              textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
            }} data-id="element-3063">
                Worse
              </span>
            </h3>

            <div className="text-lg md:text-xl text-stone-300 space-y-6 leading-relaxed" data-id="element-3064">
              <p className="text-stone-100 font-semibold" data-id="element-3065">
                Here's the thing: Even if YOU don't use AI and you don't have to
                deal with its hallucinations directly...
              </p>

              <p className="text-stone-100 font-semibold" data-id="element-3066">
                Here's the thing...
              </p>

              <p className="text-amber-500 font-bold" style={{
              textShadow: '0 0 20px rgba(217, 119, 6, 0.3)'
            }} data-id="element-3067">
                Most influencers (and even many experts) don't fact-check.
              </p>

              <div className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 space-y-3" data-id="element-3068">
                <p data-id="element-3069">They pull up ChatGPT.</p>
                <p data-id="element-3070">Ask it to summarize a topic.</p>
                <p data-id="element-3071">Record a video reading the AI script.</p>
                <p data-id="element-3072">And assume it's correct.</p>
              </div>

              <p data-id="element-3073">
                Because it{' '}
                <span className="font-bold text-stone-100 uppercase" data-id="element-3074">
                  SOUNDS
                </span>{' '}
                correct.
              </p>

              <p data-id="element-3075">
                And their audience assumes{' '}
                <span className="font-bold text-stone-100 uppercase" data-id="element-3076">THEY</span>{' '}
                did the research.
              </p>

              <p className="text-xl font-bold text-stone-100" data-id="element-3077">
                But they didn't.
              </p>

              <p className="text-xl font-bold text-stone-100" data-id="element-3078">Nobody did.</p>

              <p className="text-stone-100 italic" data-id="element-3079">
                And everyone is just putting the responsibility on someone (or
                some<span className="font-bold" data-id="element-3080">THING</span>) else.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 mt-8 sm:mt-16" data-id="element-3081">
            <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3082" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3083" />
            <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3084" />
          </div>

          {/* NEW SECTION: The Feedback Loop From Hell */}
          <div className="space-y-8" data-id="element-3085">
            <h3 className="text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
          }} data-id="element-3086">
              The Feedback Loop From{' '}
              <span className="text-red-500" style={{
              textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
            }} data-id="element-3087">
                Hell
              </span>
            </h3>

            <div className="text-lg md:text-xl text-stone-300 space-y-6 leading-relaxed" data-id="element-3088">
              <p className="text-stone-100 font-semibold" data-id="element-3089">
                Here's what's happening in real-time:
              </p>

              <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-red-600/50 text-base sm:text-lg" data-id="element-3090">
                <p data-id="element-3091">→ AI or a human shares misinformation</p>
                <p data-id="element-3092">→ Humans spread it because it sounds right</p>
                <p data-id="element-3093">→ Humans generate more content with that misinformation</p>
                <p data-id="element-3094">
                  → Other AI tools scrape that misinformation and use it to
                  generate even{' '}
                  <span className="font-bold text-stone-100 uppercase" data-id="element-3095">
                    MORE
                  </span>{' '}
                  content
                </p>
                <p data-id="element-3096">→ That content gets indexed, shared, and trusted</p>
                <p data-id="element-3097">→ The cycle repeats</p>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3098">
                Each iteration adds{' '}
                <span className="text-red-500 uppercase" data-id="element-3099">MORE</span> noise.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3100">
                Each iteration buries the truth{' '}
                <span className="text-red-500 uppercase" data-id="element-3101">DEEPER</span>.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3102">
                Each iteration makes it harder to know what's real.
              </p>

              <p className="text-xl sm:text-2xl font-bold text-center text-amber-500 mt-6 sm:mt-8" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-3103">
                And your body pays the price.
              </p>
            </div>
          </div>

          {/* Divider */}

          {/* NEW SECTION: The Era of Accelerating Misinformation */}
          <motion.div ref={ref12} initial={{
          opacity: 0,
          y: 20
        }} animate={inView12 ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4
        }} className="mt-20" data-id="element-3104">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-12" data-id="element-3105">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-red-600/50 to-red-600/50" data-id="element-3106" />
              <div className="w-2 h-2 bg-red-600 rotate-45" data-id="element-3107" />
              <div className="w-32 h-px bg-gradient-to-l from-transparent via-red-600/50 to-red-600/50" data-id="element-3108" />
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3109">
              <h3 className="text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
            }} data-id="element-3110">
                The Era of{' '}
                <span className="text-red-500" style={{
                textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
              }} data-id="element-3111">
                  Accelerating Misinformation
                </span>{' '}
                Is Already Here.
              </h3>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3112">
                Let me show you how bad it's gotten.
              </p>

              <p data-id="element-3113">You just saw that AI-generated YouTube channel.</p>

              <p data-id="element-3114">The one that hallucinated FDR onto Mount Rushmore.</p>

              <p className="text-stone-100 font-semibold" data-id="element-3115">
                That video has been viewed thousands of times.
              </p>

              <p className="text-stone-100 font-semibold" data-id="element-3116">
                And almost nobody in the comments caught the error.
              </p>

              <div className="pl-4 sm:pl-6 border-l-2 border-red-600/50 space-y-4 my-6 sm:my-8 text-base sm:text-lg" data-id="element-3117">
                <p className="text-stone-100" data-id="element-3118">
                  Which means thousands of people now "know" that FDR is on
                  Mount Rushmore.
                </p>

                <p data-id="element-3119">They'll repeat it.</p>

                <p data-id="element-3120">They'll share it.</p>

                <p data-id="element-3121">They'll use it as a "fact" in THEIR content.</p>

                <p className="text-red-400 font-bold" data-id="element-3122">
                  And the misinformation spreads.
                </p>
              </div>

              <p className="text-xl md:text-2xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3123">
                Now imagine that same process happening with peptides.
              </p>

              <div className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 space-y-3 text-base sm:text-lg" data-id="element-3124">
                <p data-id="element-3125">With dosing protocols.</p>
                <p data-id="element-3126">With drug interactions.</p>
                <p data-id="element-3127">With contraindications.</p>
                <p data-id="element-3128">With mechanisms of action.</p>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6" data-id="element-3129">
                Except instead of just getting history wrong...
              </p>

              <p className="text-xl sm:text-2xl font-bold text-red-500" style={{
              textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
            }} data-id="element-3130">
                People are putting the WRONG molecules into their bodies.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3131">
                Or worse...
              </p>

              <p className="text-lg sm:text-xl font-bold text-red-400" data-id="element-3132">
                Recommending the wrong molecules to their clients.
              </p>

              <div className="bg-red-950/20 border-l-4 border-red-600 p-4 sm:p-6 my-6 sm:my-8 space-y-4 text-base sm:text-lg" data-id="element-3133">
                <p className="text-stone-100" data-id="element-3134">
                  Based on information that was hallucinated by an AI...
                </p>
                <p className="text-stone-100" data-id="element-3135">
                  Spread by an influencer who didn't fact-check...
                </p>
                <p className="text-stone-100" data-id="element-3136">
                  Validated by MORE AI that scraped the bad information...
                </p>
                <p className="text-stone-100" data-id="element-3137">
                  And reinforced by thousands of people who believed it because
                  it SOUNDED right.
                </p>
              </div>
            </div>
          </motion.div>

          {/* NEW SECTION: Most People Are Working With the WRONG MAP */}
          <motion.div ref={ref13} initial={{
          opacity: 0,
          y: 20
        }} animate={inView13 ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4
        }} className="mt-20" data-id="element-3138">
            {/* Divider */}
            <div className="flex items-center justify-center gap-4 mb-12" data-id="element-3139">
              <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3140" />
              <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3141" />
              <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3142" />
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3143">
              <h3 className="text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-8" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
            }} data-id="element-3144">
                Most People Are Working With the{' '}
                <span className="text-amber-500" style={{
                textShadow: '0 0 30px rgba(217, 119, 6, 0.5)'
              }} data-id="element-3145">
                  WRONG MAP
                </span>
              </h3>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3146">
                Let me show you what I mean.
              </p>

              <p data-id="element-3147">
                Most people think the problem with peptides is just "bad
                information."
              </p>

              <div className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 space-y-3" data-id="element-3148">
                <p data-id="element-3149">Like if you could just filter out the noise...</p>
                <p data-id="element-3150">Find the "good sources"...</p>
                <p data-id="element-3151">Follow the "right protocols"...</p>
                <p data-id="element-3152">Then you'd be set.</p>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3153">
                But that's not how this works.
              </p>

              <p className="text-stone-100 font-semibold" data-id="element-3154">
                Because even the BEST information out there...
              </p>

              <p data-id="element-3155">Even from the most "credible" experts...</p>

              <p data-id="element-3156">
                Even from doctors with fancy credentials and big followings...
              </p>

              <p className="text-xl sm:text-2xl font-bold text-amber-500" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-3157">
                Is still missing the most important pieces.
              </p>

              <div className="bg-stone-950/50 border-2 border-amber-600/30 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3158">
                <p className="text-lg sm:text-xl font-bold text-stone-100 mb-4 sm:mb-6" data-id="element-3159">
                  Here's what they DON'T have:
                </p>

                <div className="space-y-4 text-base sm:text-lg" data-id="element-3160">
                  <div className="flex items-start gap-3" data-id="element-3161">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3162" />
                    <p className="text-stone-300" data-id="element-3163">
                      Access to and understanding of original Russian research
                      papers (most of it has NEVER been translated to English)
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3164">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3165" />
                    <p className="text-stone-300" data-id="element-3166">
                      Understanding of all three types of bioregulators (they
                      only know about Version 1)
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3167">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3168" />
                    <p className="text-stone-300" data-id="element-3169">
                      Knowledge of the "super peptides" Brenden identified
                      (bioaugmentors that almost nobody else knows exist)
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3170">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3171" />
                    <p className="text-stone-300" data-id="element-3172">
                      Real-world experience creating "impossible"
                      transformations (they're working from theory, not proven
                      results)
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3173">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3174" />
                    <p className="text-stone-300" data-id="element-3175">
                      The diagnostic frameworks that allow you to spot root
                      causes instead of just treating symptoms
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3176">
                So when they teach peptides...
              </p>

              <p data-id="element-3177">When they recommend protocols...</p>

              <p data-id="element-3178">When they answer your questions...</p>

              <p className="text-stone-100 font-semibold" data-id="element-3179">
                They're giving you the BEST answer they can...
              </p>

              <p className="text-xl sm:text-2xl font-bold text-amber-500" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-3180">
                Based on an incomplete understanding of what's actually
                possible.
              </p>

              <div className="bg-gradient-to-br from-amber-950/30 to-black border-2 border-amber-600 p-6 sm:p-8 md:p-10 mt-6 sm:mt-8 relative" data-id="element-3181">
                <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-amber-600" data-id="element-3182" />
                <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-amber-600" data-id="element-3183" />
                <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-amber-600" data-id="element-3184" />
                <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-amber-600" data-id="element-3185" />

                <p className="text-xl sm:text-2xl md:text-3xl text-center text-stone-100 font-bold leading-relaxed" style={{
                textShadow: '0 0 20px rgba(217, 119, 6, 0.3)'
              }} data-id="element-3186">
                  It's like trying to navigate with a map that's missing half
                  the territory.
                </p>
              </div>

              <p className="text-lg sm:text-xl text-stone-100 mt-6 sm:mt-8" data-id="element-3187">
                You might get SOMEWHERE.
              </p>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-amber-500" style={{
              textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
            }} data-id="element-3188">
                But you'll never reach the destinations that aren't even ON your
                map.
              </p>
            </div>
          </motion.div>

          {/* NEW SECTION: There's A Shift That MUST Happen */}
          <motion.div ref={ref14} initial={{
          opacity: 0,
          y: 20
        }} animate={inView14 ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4
        }} className="mt-12 sm:mt-20" data-id="element-3189">
            {/* Divider with blue accent */}
            <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3190">
              <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-blue-500/50" data-id="element-3191" />
              <div className="w-2 h-2 bg-blue-500 rotate-45" data-id="element-3192" />
              <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-blue-500/50 to-blue-500/50" data-id="element-3193" />
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3194">
              <h3 className="text-2xl sm:text-3xl md:text-5xl font-serif font-bold text-center text-stone-100 mb-6 sm:mb-8 leading-tight" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(59, 130, 246, 0.3)'
            }} data-id="element-3195">
                There's A Shift That{' '}
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent" style={{
                textShadow: '0 0 30px rgba(59, 130, 246, 0.5)'
              }} data-id="element-3196">
                  MUST
                </span>{' '}
                Happen
              </h3>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3197">
                Here's what most people don't understand:
              </p>

              <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-3198">
                You can't just "learn more information" and expect to operate at
                the level Brenden and Ali do.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3199">
                Because mastery isn't about VOLUME of knowledge.
              </p>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent" style={{
              textShadow: '0 0 30px rgba(59, 130, 246, 0.4)'
            }} data-id="element-3200">
                It's about the PARADIGM you're operating from.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3201">
                Let me explain.
              </p>

              <div className="bg-gradient-to-br from-blue-950/30 via-stone-950/50 to-black border-l-4 border-blue-500/50 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3202">
                <p className="text-lg sm:text-xl text-stone-100 font-bold mb-4" data-id="element-3203">
                  Right now, most people (including most "experts") are
                  operating from a FRAGMENTED paradigm.
                </p>

                <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-blue-400/30 text-base sm:text-lg" data-id="element-3204">
                  <p data-id="element-3205">They know bits and pieces.</p>
                  <p data-id="element-3206">They understand isolated mechanisms.</p>
                  <p data-id="element-3207">They can recommend protocols that "should" work.</p>
                </div>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3208">
                But they don't have the COMPLETE PICTURE.
              </p>

              <div className="pl-4 sm:pl-6 border-l-2 border-blue-500/30 space-y-3 text-base sm:text-lg" data-id="element-3209">
                <p data-id="element-3210">They've never seen the original research.</p>
                <p data-id="element-3211">They don't understand how all the pieces fit together.</p>
                <p data-id="element-3212">
                  They don't know what's ACTUALLY possible when you understand
                  peptides at the highest level.
                </p>
              </div>

              <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-3213">
                So their "best recommendations" are still limited by their
                incomplete understanding.
              </p>

              <div className="bg-gradient-to-br from-stone-950/80 via-blue-950/20 to-black border-2 border-blue-500/30 p-6 sm:p-8 my-6 sm:my-8 relative" data-id="element-3214">
                <div className="absolute top-0 left-0 w-5 sm:w-6 h-5 sm:h-6 border-l-2 border-t-2 border-blue-500/50" data-id="element-3215" />
                <div className="absolute top-0 right-0 w-5 sm:w-6 h-5 sm:h-6 border-r-2 border-t-2 border-blue-500/50" data-id="element-3216" />
                <div className="absolute bottom-0 left-0 w-5 sm:w-6 h-5 sm:h-6 border-l-2 border-b-2 border-blue-500/50" data-id="element-3217" />
                <div className="absolute bottom-0 right-0 w-5 sm:w-6 h-5 sm:h-6 border-r-2 border-b-2 border-blue-500/50" data-id="element-3218" />

                <p className="text-lg sm:text-xl md:text-2xl text-center text-stone-100 leading-relaxed italic" data-id="element-3219">
                  It's like trying to complete a 1,000-piece puzzle...
                </p>
                <p className="text-lg sm:text-xl md:text-2xl text-center text-stone-100 mt-4 leading-relaxed italic" data-id="element-3220">
                  When you've only been given 200 pieces.
                </p>
              </div>

              <p className="text-stone-100 text-base sm:text-lg" data-id="element-3221">
                You can make SOMETHING.
              </p>

              <p className="text-xl sm:text-2xl font-bold text-blue-400" style={{
              textShadow: '0 0 30px rgba(59, 130, 246, 0.4)'
            }} data-id="element-3222">
                But it'll never be the full picture.
              </p>

              <div className="bg-gradient-to-br from-blue-950/30 via-cyan-950/20 to-black border-2 border-cyan-500 p-6 sm:p-8 md:p-10 mt-8 sm:mt-12 relative" data-id="element-3223">
                <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-cyan-500" data-id="element-3224" />
                <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-cyan-500" data-id="element-3225" />
                <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-cyan-500" data-id="element-3226" />
                <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-cyan-500" data-id="element-3227" />

                <p className="text-lg sm:text-xl md:text-2xl text-stone-100 font-bold mb-4" data-id="element-3228">
                  The paradigm shift is about getting ALL the pieces.
                </p>

                <div className="space-y-3 text-base sm:text-lg" data-id="element-3229">
                  <p className="text-stone-100" data-id="element-3230">
                    It's about seeing the COMPLETE map.
                  </p>
                  <p className="text-stone-100" data-id="element-3231">
                    It's about understanding the PRINCIPLES behind the
                    protocols...
                  </p>
                  <p className="text-cyan-400 font-bold" style={{
                  textShadow: '0 0 20px rgba(34, 211, 238, 0.4)'
                }} data-id="element-3232">
                    So you can design your OWN solutions for ANY situation.
                  </p>
                </div>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3233">
                This shift is what separates the masters from everyone else.
              </p>

              <p className="text-stone-100" data-id="element-3234">
                And it's what allows Brenden and Ali to look at "hopeless"
                cases...
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3235">
                And see exactly what needs to be done.
              </p>

              <div className="bg-gradient-to-br from-blue-950/30 to-black border-2 border-blue-500/40 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3236">
                <p className="text-lg sm:text-xl text-blue-400 font-bold mb-4" data-id="element-3237">
                  During PSI PRO, Brenden and Ali are going to GIVE you this
                  paradigm shift.
                </p>

                <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-cyan-500/30" data-id="element-3238">
                  <p className="text-stone-100" data-id="element-3239">
                    They're going to show you the complete picture.
                  </p>
                  <p className="text-stone-100" data-id="element-3240">
                    They're going to reveal the principles that govern
                    biological optimization.
                  </p>
                  <p className="text-stone-100" data-id="element-3241">
                    They're going to teach you how to THINK like a master...
                  </p>
                  <p className="text-stone-100 italic" data-id="element-3242">
                    Not just follow templates like everyone else.
                  </p>
                </div>
              </div>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-cyan-400 mt-6 sm:mt-8" style={{
              textShadow: '0 0 30px rgba(34, 211, 238, 0.4)'
            }} data-id="element-3243">
                And once you make this shift...
              </p>

              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-stone-100" style={{
              textShadow: '0 0 20px rgba(59, 130, 246, 0.3)'
            }} data-id="element-3244">
                Everything changes.
              </p>

              <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-blue-500/30 mt-6 sm:mt-8 text-base sm:text-lg" data-id="element-3245">
                <p className="text-stone-100" data-id="element-3246">
                  You'll look at bloodwork differently.
                </p>
                <p className="text-stone-100" data-id="element-3247">
                  You'll design protocols differently.
                </p>
                <p className="text-stone-100" data-id="element-3248">
                  You'll solve problems differently.
                </p>
                <p className="text-stone-100 font-bold" data-id="element-3249">
                  You'll operate at a level most practitioners will NEVER reach.
                </p>
              </div>

              <p className="text-lg sm:text-xl text-stone-100 text-center mt-6 sm:mt-8 italic" data-id="element-3250">
                And you'll wonder how you ever functioned without seeing the
                full picture.
              </p>
            </div>
          </motion.div>

          {/* NEW SECTION: Accessing Powers Beyond The Mainstream */}
          <motion.div ref={ref15} initial={{
          opacity: 0,
          y: 20
        }} animate={inView15 ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4
        }} className="mt-12 sm:mt-20" data-id="element-3251">
            {/* Divider with purple/green gradient */}
            <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3252">
              <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-green-500/50" data-id="element-3253" />
              <div className="w-2 h-2 bg-gradient-to-br from-purple-500 to-green-500 rotate-45" data-id="element-3254" />
              <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-green-500/50 to-purple-500/50" data-id="element-3255" />
            </div>

            <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3256">
              <h3 className="text-2xl sm:text-3xl md:text-5xl font-serif font-bold text-center text-stone-100 mb-6 sm:mb-8 leading-tight" style={{
              fontFamily: 'Cinzel, serif',
              textShadow: '0 0 20px rgba(168, 85, 247, 0.3)'
            }} data-id="element-3257">
                This Is About Accessing{' '}
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-green-400 bg-clip-text text-transparent" style={{
                textShadow: '0 0 30px rgba(168, 85, 247, 0.5)'
              }} data-id="element-3258">
                  Powers Beyond The Mainstream
                </span>
              </h3>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3259">
                Let me be clear about what I mean.
              </p>

              <p className="text-stone-100" data-id="element-3260">
                When I say "powers beyond the mainstream"...
              </p>

              <p className="text-xl sm:text-2xl font-bold text-stone-100" data-id="element-3261">
                I'm not talking about some woo-woo mystical bullsh**t.
              </p>

              <p className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-green-400 bg-clip-text text-transparent" style={{
              textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
            }} data-id="element-3262">
                I'm talking about biological capabilities that are
                scientifically validated...
              </p>

              <p className="text-lg sm:text-xl text-stone-100 font-semibold" data-id="element-3263">
                But almost completely unknown outside of elite research circles.
              </p>

              <div className="bg-gradient-to-br from-purple-950/30 via-stone-950/50 to-green-950/20 border-2 border-purple-500/40 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3264">
                <p className="text-lg sm:text-xl font-bold text-purple-400 mb-4 sm:mb-6" data-id="element-3265">
                  Capabilities like:
                </p>

                <div className="space-y-4 text-base sm:text-lg" data-id="element-3266">
                  <div className="flex items-start gap-3" data-id="element-3267">
                    <span className="text-green-400 text-xl flex-shrink-0" data-id="element-3268">
                      →
                    </span>
                    <p className="text-stone-100" data-id="element-3269">
                      Reversing cognitive decline that neurologists said was
                      "permanent"
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3270">
                    <span className="text-green-400 text-xl flex-shrink-0" data-id="element-3271">
                      →
                    </span>
                    <p className="text-stone-100" data-id="element-3272">
                      Restoring heart function that cardiologists said required
                      a transplant
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3273">
                    <span className="text-green-400 text-xl flex-shrink-0" data-id="element-3274">
                      →
                    </span>
                    <p className="text-stone-100" data-id="element-3275">
                      Regenerating tissues that mainstream medicine considers
                      "impossible to heal"
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3276">
                    <span className="text-green-400 text-xl flex-shrink-0" data-id="element-3277">
                      →
                    </span>
                    <p className="text-stone-100" data-id="element-3278">
                      Optimizing biological markers in ways that make doctors
                      ask "what are you doing?"
                    </p>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-3279">
                    <span className="text-green-400 text-xl flex-shrink-0" data-id="element-3280">
                      →
                    </span>
                    <p className="text-stone-100" data-id="element-3281">
                      Accessing energy, clarity, and performance levels you
                      didn't know were possible
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3282">
                This isn't theory.
              </p>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3283">
                This isn't speculation.
              </p>

              <p className="text-xl sm:text-2xl font-bold text-center bg-gradient-to-r from-purple-400 to-green-400 bg-clip-text text-transparent" style={{
              textShadow: '0 0 30px rgba(168, 85, 247, 0.4)'
            }} data-id="element-3284">
                This is what happens when you understand peptides and compounds
                at the level Brenden and Ali do.
              </p>

              <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-purple-500/30 mt-6 sm:mt-8 text-base sm:text-lg" data-id="element-3285">
                <p className="text-stone-100" data-id="element-3286">
                  When you know which molecules to use.
                </p>
                <p className="text-stone-100" data-id="element-3287">
                  How to stack them for synergistic effects.
                </p>
                <p className="text-stone-100" data-id="element-3288">
                  How to dose them for maximum bioavailability.
                </p>
                <p className="text-stone-100" data-id="element-3289">
                  How to sequence them for optimal results.
                </p>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3290">
                And most importantly...
              </p>

              <div className="bg-gradient-to-br from-purple-950/30 via-green-950/20 to-black border-2 border-green-500 p-6 sm:p-8 md:p-10 mt-6 sm:mt-8 relative" data-id="element-3291">
                <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-purple-500" data-id="element-3292" />
                <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-green-500" data-id="element-3293" />
                <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-green-500" data-id="element-3294" />
                <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-purple-500" data-id="element-3295" />

                <p className="text-xl sm:text-2xl md:text-3xl text-center bg-gradient-to-r from-purple-400 via-pink-400 to-green-400 bg-clip-text text-transparent font-bold leading-relaxed" style={{
                textShadow: '0 0 30px rgba(168, 85, 247, 0.5)'
              }} data-id="element-3296">
                  When you understand the Precision Cell™ method.
                </p>
              </div>
            </div>
          </motion.div>

          {/* NEW SECTION: The Precision Cell™ Method */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="mt-20 sm:mt-32" data-id="element-3297">
            {/* Dramatic Divider with Emerald/Teal gradient */}
            <div className="flex items-center justify-center gap-4 mb-10 sm:mb-16" data-id="element-3298">
              <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-emerald-500/60 to-teal-500/60" data-id="element-3299" />
              <motion.div animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="w-3 h-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full" data-id="element-3300" />
              <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-teal-500/60 to-emerald-500/60" data-id="element-3301" />
            </div>

            {/* Main Headline - Dramatic and Eye-Catching */}
            <div className="relative mb-10 sm:mb-16" data-id="element-3302">
              {/* Glowing background effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 via-teal-500/30 to-emerald-600/20 blur-3xl" data-id="element-3303" />

              <div className="relative" data-id="element-3304">
                <h3 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-center mb-6 leading-tight" style={{
                fontFamily: 'Cinzel, serif'
              }} data-id="element-3305">
                  <span className="block bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500 bg-clip-text text-transparent" style={{
                  textShadow: '0 0 40px rgba(16, 185, 129, 0.6)'
                }} data-id="element-3306">
                    The Precision Cell™ Method:
                  </span>
                  <span className="block mt-2 sm:mt-4 text-stone-100" style={{
                  textShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
                }} data-id="element-3307">
                    The Secret Weapon for
                  </span>
                  <span className="block mt-2 bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent" style={{
                  textShadow: '0 0 40px rgba(20, 184, 166, 0.6)'
                }} data-id="element-3308">
                    Biological Transformation
                  </span>
                </h3>

                {/* Decorative underline */}
                <motion.div initial={{
                scaleX: 0
              }} animate={{
                scaleX: 1
              }} transition={{
                duration: 1,
                delay: 0.5
              }} className="w-32 sm:w-64 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent mx-auto" data-id="element-3309" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6 sm:space-y-8 text-lg md:text-xl leading-relaxed text-stone-300 max-w-4xl mx-auto" data-id="element-3310">
              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3311">
                Here's something most people don't know:
              </p>

              <p className="text-stone-100" data-id="element-3312">
                When private clients pay us $15,000 to $120,000 for
                transformation work...
              </p>

              <p className="text-stone-100 font-semibold" data-id="element-3313">
                They're not just "getting peptide protocols."
              </p>

              <div className="bg-gradient-to-br from-emerald-950/40 via-teal-950/30 to-black border-2 border-emerald-500/50 p-6 sm:p-8 my-6 sm:my-8 relative overflow-hidden" data-id="element-3314">
                {/* Subtle animated background particles */}
                <div className="absolute inset-0 opacity-20" data-id="element-3315">
                  {[...Array(6)].map((_, i) => <motion.div key={i} className="absolute w-1 h-1 bg-emerald-400 rounded-full" style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + i % 3 * 20}%`
                }} animate={{
                  y: [-10, 10, -10],
                  opacity: [0.3, 1, 0.3]
                }} transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.5,
                  ease: 'easeInOut'
                }} data-id="element-3316" />)}
                </div>

                <p className="text-lg sm:text-xl md:text-2xl text-stone-100 font-bold mb-4 relative z-10" data-id="element-3317">
                  They're getting access to a proprietary methodology that we
                  have developed over years of real-world application.
                </p>

                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center mt-6 relative z-10" data-id="element-3318">
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent" style={{
                  textShadow: '0 0 30px rgba(16, 185, 129, 0.5)'
                }} data-id="element-3319">
                    It's called the Precision Cell™ method.
                  </span>
                </p>
              </div>

              <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3320">
                And it's one of the reasons we can create transformations that
                seem impossible to everyone else.
              </p>

              {/* "Here's what it helps you to do" Section */}
              <div className="mt-8 sm:mt-12 mb-6 sm:mb-8" data-id="element-3321">
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-emerald-400 mb-6 sm:mb-8" data-id="element-3322">
                  Here's what it helps you to do:
                </p>

                {/* Benefits List - Visually Strong */}
                <div className="space-y-4 sm:space-y-6" data-id="element-3323">
                  {/* Benefit 1 */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5
                }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-emerald-950/30 to-transparent border-l-4 border-emerald-500 rounded-r-lg" data-id="element-3324">
                    <span className="text-emerald-400 text-xl sm:text-2xl flex-shrink-0 font-bold" data-id="element-3325">
                      →
                    </span>
                    <p className="text-stone-100 text-base sm:text-lg" data-id="element-3326">
                      Look at a "hopeless" case and see{' '}
                      <span className="font-bold text-emerald-400" data-id="element-3327">
                        EXACTLY
                      </span>{' '}
                      what needs to be done{' '}
                      <span className="text-stone-400 italic" data-id="element-3328">
                        (while other practitioners are still guessing)
                      </span>
                    </p>
                  </motion.div>

                  {/* Benefit 2 */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.1
                }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-teal-950/30 to-transparent border-l-4 border-teal-500 rounded-r-lg" data-id="element-3329">
                    <span className="text-teal-400 text-xl sm:text-2xl flex-shrink-0 font-bold" data-id="element-3330">
                      →
                    </span>
                    <p className="text-stone-100 text-base sm:text-lg" data-id="element-3331">
                      Design protocols that work when everything else has failed{' '}
                      <span className="text-stone-400 italic" data-id="element-3332">
                        (because you understand mechanisms at the molecular
                        level)
                      </span>
                    </p>
                  </motion.div>

                  {/* Benefit 3 */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.2
                }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-emerald-950/30 to-transparent border-l-4 border-emerald-500 rounded-r-lg" data-id="element-3333">
                    <span className="text-emerald-400 text-xl sm:text-2xl flex-shrink-0 font-bold" data-id="element-3334">
                      →
                    </span>
                    <p className="text-stone-100 text-base sm:text-lg" data-id="element-3335">
                      Reverse conditions that mainstream medicine calls
                      "permanent"{' '}
                      <span className="text-stone-400 italic" data-id="element-3336">
                        (because you know which tissues respond to which
                        peptides and how to sequence them for maximum effect)
                      </span>
                    </p>
                  </motion.div>

                  {/* Benefit 4 */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.3
                }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-teal-950/30 to-transparent border-l-4 border-teal-500 rounded-r-lg" data-id="element-3337">
                    <span className="text-teal-400 text-xl sm:text-2xl flex-shrink-0 font-bold" data-id="element-3338">
                      →
                    </span>
                    <p className="text-stone-100 text-base sm:text-lg" data-id="element-3339">
                      Optimize your biology (or your clients' biology) with the
                      kind of{' '}
                      <span className="font-bold text-teal-400" data-id="element-3340">precision</span>{' '}
                      that creates measurable, undeniable results
                    </p>
                  </motion.div>

                  {/* Benefit 5 */}
                  <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.4
                }} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-gradient-to-r from-emerald-950/30 to-transparent border-l-4 border-emerald-500 rounded-r-lg" data-id="element-3341">
                    <span className="text-emerald-400 text-xl sm:text-2xl flex-shrink-0 font-bold" data-id="element-3342">
                      →
                    </span>
                    <p className="text-stone-100 text-base sm:text-lg" data-id="element-3343">
                      Operate{' '}
                      <span className="font-bold text-emerald-400" data-id="element-3344">
                        10-20 years ahead
                      </span>{' '}
                      of mainstream medicine{' '}
                      <span className="text-stone-400 italic" data-id="element-3345">
                        (because you're not waiting for "official" validation)
                      </span>
                    </p>
                  </motion.div>
                </div>
              </div>

              {/* "This is the REAL value" Section */}
              <div className="bg-gradient-to-br from-teal-950/40 via-emerald-950/30 to-black border-2 border-teal-500/60 p-6 sm:p-8 md:p-10 my-8 sm:my-12 relative" data-id="element-3346">
                <div className="absolute top-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-t-2 border-teal-500" data-id="element-3347" />
                <div className="absolute top-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-t-2 border-emerald-500" data-id="element-3348" />
                <div className="absolute bottom-0 left-0 w-8 sm:w-10 h-8 sm:h-10 border-l-2 border-b-2 border-emerald-500" data-id="element-3349" />
                <div className="absolute bottom-0 right-0 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-b-2 border-teal-500" data-id="element-3350" />

                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center text-teal-400 mb-4 sm:mb-6" data-id="element-3351">
                  This is the REAL value.
                </p>

                <div className="space-y-3 sm:space-y-4 text-center" data-id="element-3352">
                  <p className="text-lg sm:text-xl text-stone-100" data-id="element-3353">
                    Not just knowing{' '}
                    <span className="font-bold text-emerald-400" data-id="element-3354">WHAT</span>{' '}
                    peptides to use.
                  </p>

                  <p className="text-xl sm:text-2xl font-bold text-stone-100" data-id="element-3355">
                    But understanding <span className="text-teal-400" data-id="element-3356">HOW</span>{' '}
                    to think about biology at the level Brenden and Ali do.
                  </p>
                </div>
              </div>

              {/* Final Value Proposition */}
              <div className="space-y-4 sm:space-y-6 mt-8 sm:mt-12" data-id="element-3357">
                <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3358">
                  And when you join PSI PRO...
                </p>

                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-center bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent" data-id="element-3359">
                  You're getting the complete Precision Cell™ Pro methodology.
                </p>

                <div className="bg-gradient-to-br from-emerald-950/30 to-black border-l-4 border-emerald-500 p-6 sm:p-8 space-y-3 sm:space-y-4" data-id="element-3360">
                  <p className="text-stone-100 flex items-start gap-3 text-base sm:text-lg" data-id="element-3361">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3362">✓</span>
                    <span data-id="element-3363">The same diagnostic frameworks.</span>
                  </p>
                  <p className="text-stone-100 flex items-start gap-3 text-base sm:text-lg" data-id="element-3364">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3365">✓</span>
                    <span data-id="element-3366">The same protocol design principles.</span>
                  </p>
                  <p className="text-stone-100 flex items-start gap-3 text-base sm:text-lg" data-id="element-3367">
                    <span className="text-emerald-400 flex-shrink-0" data-id="element-3368">✓</span>
                    <span data-id="element-3369">The same optimization strategies.</span>
                  </p>
                </div>

                <div className="bg-gradient-to-br from-teal-950/40 via-emerald-950/30 to-black border-2 border-emerald-500 p-6 sm:p-8 md:p-10 mt-6 sm:mt-8 relative" data-id="element-3370">
                  <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-emerald-500" data-id="element-3371" />
                  <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-teal-500" data-id="element-3372" />
                  <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-teal-500" data-id="element-3373" />
                  <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-emerald-500" data-id="element-3374" />

                  <p className="text-lg sm:text-xl md:text-2xl text-center text-stone-100 leading-relaxed" data-id="element-3375">
                    That private transformation clients paying upwards of{' '}
                    <span className="font-bold text-emerald-400 text-xl sm:text-2xl md:text-3xl" data-id="element-3376">
                      $30,000
                    </span>{' '}
                    get access to.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Divider */}

          {/* NEW SECTION: You Can't Fact-Check Everything */}
          <div className="space-y-8" data-id="element-3377"></div>

          {/* Divider */}

          {/* NEW SECTION: The Signal Is Getting Weaker */}
          <div className="space-y-8" data-id="element-3378"></div>
        </div>
      </motion.div>

      {/* NEW SECTION: Agitation - Are You Experiencing Results */}

      {/* New Section: Misinformation Crisis */}
      <motion.div ref={ref3} initial={{
      opacity: 0,
      y: 20
    }} animate={inView3 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-20 sm:mt-32" data-id="element-3379">
        {/* Decorative divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3380">
          <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3381" />
          <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-amber-500" data-id="element-3382" />
          <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3383" />
        </div>

        {/* Main headline - Split into two parts */}
        <div className="mb-8 sm:mb-12 text-center" data-id="element-3384">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-serif text-stone-100 mb-4 sm:mb-6 tracking-wide" style={{
          fontFamily: 'Cinzel, serif'
        }} data-id="element-3385">
            What Does This Look Like When You HAVEN'T Made These Paradigm
            Shifts?
          </h2>

          <h2 className="text-2xl sm:text-4xl md:text-6xl font-serif text-amber-500 leading-tight font-bold" style={{
          fontFamily: 'Cinzel, serif',
          textShadow: '0 0 40px rgba(217, 119, 6, 0.4)'
        }} data-id="element-3386">
            {' '}
            <span className="text-red-500 drop-shadow-lg" style={{
            textShadow: '0 0 30px rgba(220, 38, 38, 0.5)'
          }} data-id="element-3387">
              6 of the Most Common Lies Being Spread About Peptides
            </span>
          </h2>
        </div>

        {/* Sub-headline */}

        {/* Normal text */}
        <div className="space-y-4 sm:space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3388">
          <p className="text-stone-100 font-semibold text-base sm:text-lg" data-id="element-3389">
            See if you've heard people say any of the following...
          </p>
        </div>
      </motion.div>

      {/* NEW SECTION: Classic Misinformation #1 - ENHANCED HEADER */}
      <motion.div ref={ref4} initial={{
      opacity: 0,
      y: 20
    }} animate={inView4 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3390">
        {/* Decorative Divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3391">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3392" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3393" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3394" />
        </div>

        {/* DRAMATICALLY ENHANCED HEADLINE */}
        <div className="relative mb-8 sm:mb-12" data-id="element-3395">
          {/* Intense glow background */}
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3396" />

          {/* Main header container */}
          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3397">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3398">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3399" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3400" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3401" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3402">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3403" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3404" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3405" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3406">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3407" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3408" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3409" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3410">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3411" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3412" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3413" />
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3414">
              <div className="relative" data-id="element-3415">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3416" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3417" />
              </div>
            </div>

            {/* Number badge */}
            <div className="text-center mb-4" data-id="element-3418">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3419">
                Classic Misinformation Spread In The Peptide World #1
              </span>
            </div>

            {/* Main title */}
            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3420">
              The "Peptides & Small Molecule Drugs Are the Same" Myth
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3421">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-3422" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3423">
            <p data-id="element-3424">
              Have you ever heard of the compound{' '}
              <span className="text-stone-100 font-semibold" data-id="element-3425">SLU-PP-332</span>?
              Or perhaps{' '}
              <span className="text-stone-100 font-semibold" data-id="element-3426">MK-677</span>? How
              about Tesofensine, BAM15 or PEA?
            </p>

            <p className="font-medium text-amber-500/90 italic" data-id="element-3427">
              If you have, here's the question:
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3428">
              Have you heard people call them peptides?
            </p>

            <p data-id="element-3429">
              I know of world renowned doctors and influencers with hundreds of
              thousands of followers that continually push this nonsense.
            </p>

            <p data-id="element-3430">I have to wonder if they do it intentionally.</p>

            <p data-id="element-3431">
              Because they will often position peptides and bioregulators as
              having "no side effects" (which can be true when they're properly
              classified, for example Khavinson's bioregulators seem to be
              largely side-effect free even up to extreme dosages).
            </p>

            <div className="flex gap-4 items-start bg-red-950/20 p-4 sm:p-6 border border-red-900/30 rounded-sm my-6 sm:my-8" data-id="element-3432">
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-3433" />
              <div data-id="element-3434">
                <p className="text-stone-100 font-bold text-base sm:text-lg mb-2" data-id="element-3435">
                  But those compounds are NOT peptides...
                </p>
                <p className="text-red-200/80 text-sm sm:text-base" data-id="element-3436">
                  ...and they can have{' '}
                  <span className="text-red-400 font-bold uppercase tracking-wide" data-id="element-3437">
                    EXTREME side-effects
                  </span>{' '}
                  when used incorrectly.
                </p>
              </div>
            </div>

            <p className="text-sm sm:text-base text-stone-400 italic pl-4 sm:pl-6 border-l border-stone-700" data-id="element-3438">
              (There is also very minimal human safety data on many of them.
              With many of the dosages being recommended, when properly
              converted for humans, simply being impossible to even be
              effective.)
            </p>

            <p data-id="element-3439">
              But they pass them off as if they're all peptides, and they make
              the compounds look better than they are.
            </p>

            <p className="text-stone-100 font-medium" data-id="element-3440">
              Which results in people paying premium prices for inferior
              products, often implementing protocols that can't possibly work at
              the suggested dosages, putting their organs at risk of being
              damaged, and questioning why they aren't getting results.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Classic Misinformation #2 - ENHANCED HEADER */}
      <motion.div ref={ref5} initial={{
      opacity: 0,
      y: 20
    }} animate={inView5 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3441">
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3442">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3443" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3444" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3445" />
        </div>

        <div className="relative mb-8 sm:mb-12" data-id="element-3446">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3447" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3448">
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3449">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3450" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3451" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3452" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3453">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3454" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3455" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3456" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3457">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3458" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3459" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3460" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3461">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3462" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3463" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3464" />
            </div>

            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3465">
              <div className="relative" data-id="element-3466">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3467" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3468" />
              </div>
            </div>

            <div className="text-center mb-4" data-id="element-3469">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3470">
                Classic Misinformation Spread In The Peptide World #2
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3471">
              The "Peptides Don't Work Orally" Fallacy
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3472">
          <div className="absolute top-0 left-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 -translate-x-1/2" data-id="element-3473" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3474">
            <p data-id="element-3475">
              Here's how you instantly identify someone who doesn't actually
              understand peptides:
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3476">
              They say "peptides don't work orally."
            </p>

            <p data-id="element-3477">
              There are multiple doctors out there spreading this idea with
              confidence.
            </p>

            <div className="flex gap-4 items-start bg-red-950/20 p-4 sm:p-6 border border-red-900/30 rounded-sm my-6 sm:my-8" data-id="element-3478">
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-3479" />
              <div data-id="element-3480">
                <p className="text-stone-100 font-bold text-base sm:text-lg mb-2" data-id="element-3481">
                  But it's COMPLETELY wrong.
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start bg-green-950/20 p-4 sm:p-6 border border-green-900/30 rounded-sm my-6 sm:my-8" data-id="element-3482">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" data-id="element-3483" />
              <div data-id="element-3484">
                <p className="text-stone-100 font-bold text-base sm:text-lg mb-2" data-id="element-3485">
                  The truth:
                </p>
                <p className="text-green-200/80 text-sm sm:text-base" data-id="element-3486">
                  Many peptides{' '}
                  <span className="text-green-400 font-bold uppercase tracking-wide" data-id="element-3487">
                    DO work orally
                  </span>
                  . But many peptides also do NOT work orally. It's not black &
                  white.
                </p>
              </div>
            </div>

            <p data-id="element-3488">
              The key is understanding the specific characteristics that
              determine oral bioavailability, and ultimately knowing which ones
              will work compared to which ones won't.
            </p>

            <p data-id="element-3489">
              When someone makes blanket statements about oral peptides, they're
              showing you they don't understand the biological mechanisms behind
              how they work.
            </p>

            <p className="text-stone-100 font-medium italic border-l border-stone-700 pl-4 sm:pl-6 text-sm sm:text-base" data-id="element-3490">
              They're regurgitating oversimplified "rules" instead of operating
              from mechanistic understanding.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Classic Misinformation #3 - ENHANCED HEADER */}
      <motion.div ref={ref6} initial={{
      opacity: 0,
      y: 20
    }} animate={inView6 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3491">
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3492">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3493" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3494" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3495" />
        </div>

        <div className="relative mb-8 sm:mb-12" data-id="element-3496">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3497" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3498">
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3499">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3500" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3501" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3502" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3503">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3504" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3505" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3506" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3507">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3508" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3509" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3510" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3511">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3512" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3513" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3514" />
            </div>

            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3515">
              <div className="relative" data-id="element-3516">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3517" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3518" />
              </div>
            </div>

            <div className="text-center mb-4" data-id="element-3519">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3520">
                Classic Misinformation Spread In The Peptide World #3
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3521">
              The Timing Protocol That's Destroying Your Absorption
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3522">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-3523" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3524">
            <p data-id="element-3525">
              Even the official manufacturer's recommendations for when to take
              peptide capsules are actually harmful for absorption.
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3526">
              The standard advice of "take with food" is NOT based on
              bioavailability or effectiveness.
            </p>

            <div className="flex gap-4 items-start bg-amber-950/20 p-4 sm:p-6 border border-amber-900/30 rounded-sm my-6 sm:my-8" data-id="element-3527">
              <Clock className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3528" />
              <div data-id="element-3529">
                <p className="text-stone-100 font-bold text-base sm:text-lg mb-2" data-id="element-3530">
                  The reality:
                </p>
                <p className="text-amber-200/80 text-sm sm:text-base" data-id="element-3531">
                  Taking oral peptides with food (particularly protein) is going
                  to{' '}
                  <span className="text-red-400 font-bold uppercase tracking-wide" data-id="element-3532">
                    reduce its effectiveness
                  </span>
                  .
                </p>
              </div>
            </div>

            <p data-id="element-3533">
              In fact, Brenden Henry specifically recommends taking orally
              bioavailable peptides on an{' '}
              <span className="text-stone-100 font-bold uppercase" data-id="element-3534">
                EMPTY STOMACH
              </span>{' '}
              (the exact opposite of most recommendations) for maximum
              absorption.
            </p>

            <p className="pl-4 sm:pl-6 border-l-2 border-amber-600/30 text-sm sm:text-base" data-id="element-3535">
              <span className="text-amber-500 font-bold" data-id="element-3536">Why?</span> Because
              when proteins are in your stomach, they will compete with peptides
              for absorption via LAT and PEPT transporters.
            </p>

            <p data-id="element-3537">
              The official dosing information on many boxes is simply not
              optimal.
            </p>

            <p data-id="element-3538">
              And for the "experts" who DO know you can dose peptides orally,
              they're just parroting advice that's literally reducing the
              effectiveness of your protocols.
            </p>

            <p className="text-stone-100 font-medium italic border-l border-stone-700 pl-4 sm:pl-6 text-sm sm:text-base" data-id="element-3539">
              But you have no way of knowing this unless you've studied the
              original absorption kinetics research.
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-3540">
              Brenden has. You haven't. Neither has anyone else teaching
              peptides online.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Classic Misinformation #4 - ENHANCED HEADER */}
      <motion.div ref={ref7} initial={{
      opacity: 0,
      y: 20
    }} animate={inView7 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3541">
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3542">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3543" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3544" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3545" />
        </div>

        <div className="relative mb-8 sm:mb-12" data-id="element-3546">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3547" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3548">
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3549">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3550" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3551" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3552" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3553">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3554" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3555" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3556" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3557">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3558" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3559" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3560" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3561">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3562" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3563" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3564" />
            </div>

            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3565">
              <div className="relative" data-id="element-3566">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3567" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3568" />
              </div>
            </div>

            <div className="text-center mb-4" data-id="element-3569">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3570">
                Classic Misinformation Spread In The Peptide World #4
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3571">
              The False "BPC-157 Is the Best Healing Peptide" Narrative
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3572">
          <div className="absolute top-0 left-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 -translate-x-1/2" data-id="element-3573" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3574">
            <p data-id="element-3575">
              Here's something that will shock most people in the peptide
              community:
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3576">
              We do NOT generally recommend BPC-157 for healing.
            </p>

            <p data-id="element-3577">Not because it doesn't work.</p>

            <p data-id="element-3578">
              But because there's a{' '}
              <span className="text-amber-500 font-bold" data-id="element-3579">
                vastly superior, yet basically unknown peptide
              </span>{' '}
              we recommend instead for most healing protocols.
            </p>

            <div className="bg-stone-950/50 p-4 sm:p-6 border border-stone-800 rounded-sm my-6 sm:my-8" data-id="element-3580">
              <p className="text-stone-100 font-bold mb-4 flex items-center gap-2 text-base sm:text-lg" data-id="element-3581">
                <CheckCircle className="w-5 h-5 text-green-500" data-id="element-3582" />
                This superior peptide has:
              </p>
              <ul className="space-y-3 text-sm sm:text-base" data-id="element-3583">
                <li className="flex items-start gap-3" data-id="element-3584">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3585" />
                  <span data-id="element-3586">Better tissue regeneration properties</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3587">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3588" />
                  <span data-id="element-3589">Fewer side effect risks</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3590">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3591" />
                  <span data-id="element-3592">More comprehensive healing mechanisms</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3593">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3594" />
                  <span data-id="element-3595">FAR MORE research and safety data in humans</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3596">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3597" />
                  <span data-id="element-3598">
                    Can be taken even in extremely high dosages with basically
                    NO negative effects seen
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-3599">
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2.5 flex-shrink-0" data-id="element-3600" />
                  <span data-id="element-3601">
                    The ability to enhance your biological function at the DNA
                    level
                  </span>
                </li>
              </ul>
            </div>

            <p data-id="element-3602">
              But almost nobody knows about it because they're stuck in the
              BPC-157 echo chamber.
            </p>

            <p data-id="element-3603">
              Influencers teach BPC-157 because it's what everyone else teaches.
            </p>

            <p data-id="element-3604">
              Practitioners recommend BPC-157 because it's what they learned
              from those influencers.
            </p>

            <div className="flex gap-4 items-start bg-red-950/20 p-4 sm:p-6 border border-red-900/30 rounded-sm my-6 sm:my-8" data-id="element-3605">
              <ShieldAlert className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-3606" />
              <div data-id="element-3607">
                <p className="text-red-200/80 text-sm sm:text-base" data-id="element-3608">
                  Meanwhile, you're using a second-tier healing peptide with the
                  potential for some{' '}
                  <span className="text-red-400 font-bold uppercase tracking-wide" data-id="element-3609">
                    harrowing side-effects
                  </span>{' '}
                  (like anhedonia) when a superior option exists.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Classic Misinformation #5 - ENHANCED HEADER */}
      <motion.div ref={ref8} initial={{
      opacity: 0,
      y: 20
    }} animate={inView8 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3610">
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3611">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3612" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3613" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3614" />
        </div>

        <div className="relative mb-8 sm:mb-12" data-id="element-3615">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3616" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3617">
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3618">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3619" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3620" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3621" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3622">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3623" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3624" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3625" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3626">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3627" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3628" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3629" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3630">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3631" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3632" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3633" />
            </div>

            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3634">
              <div className="relative" data-id="element-3635">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3636" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3637" />
              </div>
            </div>

            <div className="text-center mb-4" data-id="element-3638">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3639">
                Classic Misinformation Spread In The Peptide World #5
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3640">
              The Growth Hormone Releasing Peptide Paradox
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3641">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 translate-x-1/2" data-id="element-3642" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3643">
            <p data-id="element-3644">
              This is the one that proves beyond doubt how surface-level most
              "expert" knowledge is:
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3645">
              Growth Hormone Releasing Peptides like sermorelin, ipamorelin and
              CJC-1295 might be the WORST choice if you want to increase growth
              hormone levels.
            </p>

            <p className="font-bold text-amber-500 italic" data-id="element-3646">Read that again.</p>

            <p data-id="element-3647">
              The peptides specifically marketed for increasing growth hormone
              might be the worst option for that goal.
            </p>

            <p data-id="element-3648">
              <span className="text-stone-100 font-bold" data-id="element-3649">Why?</span>
            </p>

            <div className="flex gap-4 items-start bg-red-950/20 p-4 sm:p-6 border border-red-900/30 rounded-sm my-6 sm:my-8" data-id="element-3650">
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-3651" />
              <div data-id="element-3652">
                <p className="text-red-200/80 text-sm sm:text-base" data-id="element-3653">
                  Because every single one of these options brings a whole host
                  of potential risks, side-effects and extra responsibilities to
                  make sure you do it properly...
                </p>
                <p className="text-stone-100 font-bold mt-2 text-base sm:text-lg" data-id="element-3654">
                  While resulting in WORSE effects than the superior option.
                </p>
              </div>
            </div>

            <p data-id="element-3655">
              Yet these are ultimately the most well-known options in the
              peptide world when it comes to increasing growth hormone levels.
            </p>

            <p className="text-stone-100 font-medium italic border-l border-stone-700 pl-4 sm:pl-6 text-sm sm:text-base" data-id="element-3656">
              Unless you understand these mechanisms at the molecular level,
              you're implementing protocols that sound right but deliver
              suboptimal results.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Classic Misinformation #6 - ENHANCED HEADER */}
      <motion.div ref={ref9} initial={{
      opacity: 0,
      y: 20
    }} animate={inView9 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-12 sm:mt-20" data-id="element-3657">
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3658">
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3659" />
          <div className="w-3 h-3 bg-amber-600 rotate-45 flex-shrink-0" data-id="element-3660" />
          <div className="w-full h-px bg-gradient-to-r from-transparent via-amber-600/30 to-transparent" data-id="element-3661" />
        </div>

        <div className="relative mb-8 sm:mb-12" data-id="element-3662">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/20 via-amber-500/30 to-amber-600/20 blur-2xl animate-pulse-slow" data-id="element-3663" />

          <div className="relative bg-gradient-to-br from-amber-950/60 via-stone-950/80 to-black border-4 border-amber-600/60 p-6 sm:p-8 md:p-10 shadow-[0_0_60px_rgba(217,119,6,0.5)]" data-id="element-3664">
            <div className="absolute top-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3665">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3666" />
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3667" />
              <div className="absolute top-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" data-id="element-3668" />
            </div>
            <div className="absolute top-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3669">
              <div className="absolute top-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3670" />
              <div className="absolute top-0 right-0 w-1 h-full bg-gradient-to-b from-amber-500 to-transparent" data-id="element-3671" />
              <div className="absolute top-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '0.5s'
            }} data-id="element-3672" />
            </div>
            <div className="absolute bottom-0 left-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3673">
              <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500 to-transparent" data-id="element-3674" />
              <div className="absolute bottom-0 left-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3675" />
              <div className="absolute bottom-2 left-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1s'
            }} data-id="element-3676" />
            </div>
            <div className="absolute bottom-0 right-0 w-8 sm:w-12 h-8 sm:h-12" data-id="element-3677">
              <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-amber-500 to-transparent" data-id="element-3678" />
              <div className="absolute bottom-0 right-0 w-1 h-full bg-gradient-to-t from-amber-500 to-transparent" data-id="element-3679" />
              <div className="absolute bottom-2 right-2 w-3 h-3 bg-amber-500 rotate-45 animate-pulse" style={{
              animationDelay: '1.5s'
            }} data-id="element-3680" />
            </div>

            <div className="flex justify-center mb-4 sm:mb-6" data-id="element-3681">
              <div className="relative" data-id="element-3682">
                <div className="absolute inset-0 bg-amber-500/40 blur-xl animate-pulse-slow" data-id="element-3683" />
                <AlertOctagon className="relative w-10 h-10 sm:w-12 sm:h-12 text-amber-400 drop-shadow-[0_0_15px_rgba(251,191,36,0.8)]" data-id="element-3684" />
              </div>
            </div>

            <div className="text-center mb-4" data-id="element-3685">
              <span className="inline-block px-4 sm:px-6 py-2 bg-amber-600/30 border-2 border-amber-500/50 text-amber-400 font-bold tracking-[0.2em] sm:tracking-[0.3em] uppercase text-sm sm:text-lg md:text-xl" style={{
              textShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
            }} data-id="element-3686">
                Classic Misinformation Spread In The Peptide World #6
              </span>
            </div>

            <h4 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center text-amber-100 leading-tight font-bold" style={{
            fontFamily: 'Cinzel, serif',
            textShadow: '0 0 40px rgba(217, 119, 6, 0.8), 0 0 60px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3687">
              The Bioregulator Blind Spot: Almost Nobody Knows They Exist
            </h4>
          </div>
        </div>

        {/* Content Box */}
        <div className="bg-stone-900/40 border border-stone-800 p-6 sm:p-8 md:p-10 rounded-sm relative overflow-hidden" data-id="element-3688">
          <div className="absolute top-0 left-0 w-64 h-64 bg-amber-900/10 blur-3xl rounded-full -translate-y-1/2 -translate-x-1/2" data-id="element-3689" />

          <div className="space-y-4 sm:space-y-6 text-base sm:text-lg text-stone-300 relative z-10" data-id="element-3690">
            <p data-id="element-3691">
              This is the most shocking oversight in the entire peptide
              ecosystem:
            </p>

            <p className="text-lg sm:text-xl text-stone-100 font-bold border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/30 to-transparent" data-id="element-3692">
              Most people in the peptide world have never even heard of
              Khavinson's bioregulator peptides.
            </p>

            <p data-id="element-3693">
              They talk endlessly about BPC-157, TB-500, CJC-1295, ipamorelin,
              semaglutide... yet remain completely oblivious to the category of
              peptides that are some of the most powerful, safest, and
              biologically transformative compounds humans have ever discovered.
            </p>

            <div className="bg-stone-950/50 p-4 sm:p-6 border border-stone-800 rounded-sm my-6 sm:my-8" data-id="element-3694">
              <div className="flex items-center gap-3 mb-4" data-id="element-3695">
                <Dna className="w-6 h-6 text-amber-500" data-id="element-3696" />
                <p className="text-stone-100 font-bold text-base sm:text-lg" data-id="element-3697">
                  Bioregulators are not "just another type of peptide."
                </p>
              </div>
              <p className="text-stone-300 pl-9 text-sm sm:text-base" data-id="element-3698">
                They operate differently. They target the genome itself. They
                influence tissue at the level of DNA transcription. They don't
                stimulate—they normalize. They don't push the body—they instruct
                it. They are an entirely different class of biological
                intelligence.
              </p>
            </div>

            <p data-id="element-3699">And yet...</p>

            <p data-id="element-3700">The peptide mainstream has absolutely no idea they exist.</p>

            <p data-id="element-3701">
              Even the so-called experts (educators, influencers, doctors with
              massive followings) are teaching an incomplete universe of
              peptides. Their map is missing the most valuable territory.
            </p>

            {/* NEW: Doctor YouTube Example Image */}
            <div className="relative my-8" data-id="element-3702">
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600/20 to-amber-600/20 rounded-lg blur" data-id="element-3703" />
              <div className="relative bg-stone-950 p-2 rounded-lg border border-red-600/30" data-id="element-3704">
                <img src="https://cdn.magicpatterns.com/uploads/piPwtgJdRX7h6R5ZWny4D1/image.png" alt="Doctor on YouTube ranking peptides" className="w-full rounded" data-id="element-3705" />
                <p className="text-sm sm:text-base text-stone-300 mt-3 px-2 leading-relaxed" data-id="element-3706">
                  Ignoring the fact he spelled ipamorelin wrong, this is a
                  doctor on Youtube with a video that has over 100k views,
                  giving epitalon the LOWEST ranking possible. All because he
                  thinks there hasn't been enough legitimate research on it. But{' '}
                  <span className="font-bold text-stone-100" data-id="element-3707">
                    epitalon has more studies proving its effectiveness in
                    humans than others he ranked higher
                  </span>
                  . It SHOULD be an A or even S-tier peptide - he ranked it F!
                </p>
              </div>
            </div>

            <p className="text-stone-100 font-bold mt-8" data-id="element-3708">But it goes deeper:</p>

            <p data-id="element-3709">
              Among the microscopic percentage of practitioners who do know
              about bioregulators, almost every single one of them only
              understands Version 1 bioregulator products.
            </p>

            <p className="text-amber-500 font-medium italic" data-id="element-3710">
              The lowest-tier, least evolved, bare-minimum formulations.
            </p>

            <div className="space-y-4 my-6 sm:my-8 pl-4 border-l-2 border-stone-700 text-sm sm:text-base" data-id="element-3711">
              <div data-id="element-3712">
                <p className="text-stone-100 font-bold" data-id="element-3713">
                  Version 1 = The bare minimum required to work
                </p>
                <p className="text-stone-400 text-xs sm:text-sm" data-id="element-3714">
                  (This is what everyone talks about. It's entry-level.)
                </p>
              </div>

              <div data-id="element-3715">
                <p className="text-stone-100 font-bold" data-id="element-3716">
                  Version 2 = A dramatically more potent biological signal
                </p>
                <p className="text-stone-400 text-xs sm:text-sm" data-id="element-3717">
                  (Rare, unknown, and exponentially more effective.)
                </p>
              </div>

              <div data-id="element-3718">
                <p className="text-stone-100 font-bold" data-id="element-3719">
                  Version 3 = So powerful they must be used with extreme caution
                </p>
                <p className="text-stone-400 text-xs sm:text-sm" data-id="element-3720">
                  (Nearly nobody has access, understanding, or safe dosing
                  knowledge.)
                </p>
              </div>
            </div>

            <div className="flex gap-4 items-start bg-amber-950/30 p-4 sm:p-6 border border-amber-600/50 rounded-sm my-6 sm:my-8" data-id="element-3721">
              <Zap className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" data-id="element-3722" />
              <div data-id="element-3723">
                <p className="text-stone-100 font-bold text-base sm:text-lg leading-relaxed" data-id="element-3724">
                  Peptide Mastery 2.0 is the ONLY place where you will discover
                  about all three versions and how to apply them in-depth, at a
                  level beyond anything else in the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* NEW SECTION: Classic Misinformation #7 */}
      <motion.div ref={ref10} initial={{
      opacity: 0,
      y: 20
    }} animate={inView10 ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.4
    }} className="relative z-10 max-w-4xl mx-auto mt-20 sm:mt-32" data-id="element-3725">
        {/* Decorative Divider */}
        <div className="flex items-center justify-center gap-4 mb-8 sm:mb-12" data-id="element-3726">
          <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3727" />
          <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3728" />
          <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3729" />
        </div>

        {/* Headline with "problem" in red */}
        <h2 className="text-2xl sm:text-3xl md:text-5xl font-serif text-center mb-8 sm:mb-12 leading-tight" style={{
        fontFamily: 'Cinzel, serif'
      }} data-id="element-3730">
          <span className="block text-amber-500 font-bold mb-3" style={{
          textShadow: '0 0 40px rgba(217, 119, 6, 0.5)'
        }} data-id="element-3731">
            The{' '}
            <span className="relative inline-block" data-id="element-3732">
              <span className="text-red-500" style={{
              textShadow: '0 0 50px rgba(239, 68, 68, 0.8), 0 0 80px rgba(220, 38, 38, 0.4)'
            }} data-id="element-3733">
                Problem
              </span>
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent opacity-60 blur-sm" data-id="element-3734" />
            </span>{' '}
            With All This:
          </span>
        </h2>

        {/* Body content */}
        <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-3735">
          <p className="text-stone-100 font-semibold text-lg sm:text-xl border-l-4 border-amber-600 pl-4 sm:pl-6 py-2 bg-gradient-to-r from-amber-950/20 to-transparent" data-id="element-3736">
            The information you're relying on — even from "qualified experts" —
            is incomplete, oversimplified, or flat-out wrong.
          </p>

          <p className="text-stone-100" data-id="element-3737">
            And this isn't just about missing optimal results.
          </p>

          <p className="font-bold text-stone-100" data-id="element-3738">
            This is about making mistakes that compound over months and years.
          </p>

          <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-red-600/50 text-base sm:text-lg" data-id="element-3739">
            <p data-id="element-3740">
              <span className="text-red-400 font-bold" data-id="element-3741">
                Mistakes that silently damage your biology
              </span>{' '}
              while you think you're optimizing it.
            </p>

            <p data-id="element-3742">
              <span className="text-red-400 font-bold" data-id="element-3743">
                Mistakes that waste thousands of dollars
              </span>{' '}
              on ineffective products and protocols.
            </p>

            <p data-id="element-3744">
              <span className="text-red-400 font-bold" data-id="element-3745">
                Mistakes that cost you the irreplaceable resource of time
              </span>{' '}
              as your biology continues to decay.
            </p>
          </div>

          <p className="text-xl sm:text-2xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3746">
            Or worse...
          </p>

          <p className="text-red-400 font-bold" data-id="element-3747">
            Mistakes that damage the trust of the people who rely on YOU for
            guidance.
          </p>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-8 sm:py-12 my-8 sm:my-12" data-id="element-3748">
            <div className="w-16 sm:w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3749" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-3750" />
            <div className="w-16 sm:w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-3751" />
          </div>

          {/* NEW SECTION: But You Can't Just Solve This With "Better Information" */}
          <h3 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-center text-stone-100 mb-6 sm:mb-8" style={{
          fontFamily: 'Cinzel, serif',
          textShadow: '0 0 20px rgba(217, 119, 6, 0.2)'
        }} data-id="element-3752">
            But You Can't Just Solve This With{' '}
            <span className="text-amber-500" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.5)'
          }} data-id="element-3753">
              "Better Information"
            </span>
          </h3>

          <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3754">
            Here's what most people don't understand:
          </p>

          <p className="text-stone-100" data-id="element-3755">
            You can't solve this problem by just "finding better sources."
          </p>

          <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3756">
            Because even the BEST sources out there are still operating with an
            incomplete paradigm.
          </p>

          <div className="space-y-4 pl-4 sm:pl-6 border-l-2 border-amber-600/30 text-base sm:text-lg" data-id="element-3757">
            <p data-id="element-3758">They're teaching you a FRACTION of what's possible.</p>
            <p data-id="element-3759">They're giving you a MAP that's missing half the territory.</p>
          </div>

          <p className="text-stone-100" data-id="element-3760">
            And no amount of "doing your own research" is going to fix that...
          </p>

          <p className="text-lg sm:text-xl font-bold text-stone-100" data-id="element-3761">
            Unless you're willing to:
          </p>

          <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-stone-700 text-base sm:text-lg" data-id="element-3762">
            <p className="flex items-start gap-3" data-id="element-3763">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3764">→</span>
              <span data-id="element-3765">Learn Russian</span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3766">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3767">→</span>
              <span data-id="element-3768">
                Track down obscure military and academic journals from the
                Soviet era
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3769">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3770">→</span>
              <span data-id="element-3771">
                Translate hundreds of studies that have never been brought to
                the West
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3772">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3773">→</span>
              <span data-id="element-3774">
                Spend years understanding peptide mechanisms at the molecular
                level
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3775">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3776">→</span>
              <span data-id="element-3777">
                Work with enough real human cases to know what actually works
                vs. what just sounds good in theory
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3778">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3779">→</span>
              <span data-id="element-3780">
                Develop your own diagnostic frameworks for identifying root
                causes
              </span>
            </p>
            <p className="flex items-start gap-3" data-id="element-3781">
              <span className="text-amber-500 flex-shrink-0" data-id="element-3782">→</span>
              <span data-id="element-3783">
                Create your own protocol design methodology based on synergistic
                effects
              </span>
            </p>
          </div>

          <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3784">
            In other words...
          </p>

          <p className="text-xl sm:text-2xl font-bold text-amber-500" data-id="element-3785">
            Unless you're willing to do what Brenden Henry has done.
          </p>

          <div className="space-y-3 pl-4 sm:pl-6 border-l-2 border-amber-600/30 text-base sm:text-lg" data-id="element-3786">
            <p data-id="element-3787">Which took him years of effort.</p>
            <p data-id="element-3788">And thousands of hours of obsessive research.</p>
            <p data-id="element-3789">And real-world application with over 1,500 participants.</p>
            <p data-id="element-3790">
              And private transformation work with clients paying $15K−$120K.
            </p>
          </div>

          <p className="text-lg sm:text-xl font-bold text-stone-100 mt-6 sm:mt-8" data-id="element-3791">
            So here's the reality:
          </p>

          <p className="text-stone-100" data-id="element-3792">You have two options.</p>

          <div className="bg-gradient-to-br from-stone-950/60 to-black border-2 border-stone-700 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3793">
            <p className="text-lg sm:text-xl font-bold text-stone-100 mb-4" data-id="element-3794">
              Option 1:
            </p>
            <p className="text-stone-300 text-base sm:text-lg" data-id="element-3795">
              Spend the next 10+ years trying to figure this out on your own.
            </p>
            <p className="text-stone-400 text-sm mt-2 italic" data-id="element-3796">
              (And hope you don't make too many costly mistakes along the way.)
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-950/30 to-black border-2 border-amber-600/50 p-6 sm:p-8 my-6 sm:my-8" data-id="element-3797">
            <p className="text-lg sm:text-xl font-bold text-amber-400 mb-4" data-id="element-3798">
              Option 2:
            </p>
            <p className="text-stone-100 text-base sm:text-lg" data-id="element-3799">
              Get direct access to the people who've already done the work.
            </p>
            <p className="text-stone-100 text-base sm:text-lg" data-id="element-3800">
              Who've already made the paradigm shift.
            </p>
            <p className="text-stone-100 text-base sm:text-lg" data-id="element-3801">
              Who already KNOW what's possible.
            </p>
            <p className="text-stone-100 text-base sm:text-lg" data-id="element-3802">
              And who are willing to share it with you.
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-950/30 to-black border-2 border-amber-600 p-6 sm:p-8 md:p-10 mt-6 sm:mt-8 relative" data-id="element-3803">
            <div className="absolute top-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-t-2 border-amber-600" data-id="element-3804" />
            <div className="absolute top-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-t-2 border-amber-600" data-id="element-3805" />
            <div className="absolute bottom-0 left-0 w-6 sm:w-8 h-6 sm:h-8 border-l-2 border-b-2 border-amber-600" data-id="element-3806" />
            <div className="absolute bottom-0 right-0 w-6 sm:w-8 h-6 sm:h-8 border-r-2 border-b-2 border-amber-600" data-id="element-3807" />

            <p className="text-xl sm:text-2xl md:text-3xl text-center font-bold text-amber-500 leading-relaxed" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
          }} data-id="element-3808">
              That's what PSI PRO — The Private Research Order is.
            </p>
          </div>

          <div className="text-center mt-10 sm:mt-14" data-id="element-3809">
            <button onClick={() => {
            const pricingSection = document.querySelector('[data-pricing-section]');
            if (pricingSection) {
              pricingSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              });
            }
          }} className="inline-block px-8 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-black font-bold text-lg sm:text-xl rounded-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] hover:shadow-[0_0_50px_rgba(217,119,6,0.6)] hover:scale-105 transition-all duration-300 min-h-[44px]" data-id="element-3810">
              Join the Private Research Order Now
            </button>
          </div>
        </div>
      </motion.div>
    </section>;
}